import { Flex } from '@everlywell/ui-kit';
import ReviewsCard from 'components/ReviewsCard';
import React, { useEffect, useState } from 'react';
import { AGGREGATE_POWER_REVIEWS_PRODUCT_ID } from 'utils/constants/powerReviews';

import SectionContainer from '../components/SectionContainer';
import SectionHeader from '../components/SectionHeader';
import ReviewsContainer from './components/ReviewsContainer';
import StatCard from './components/StatCard';
import usePowerReviewsById from './hooks/usePowerReviewsById';

type StatCardSlots = NonNullable<React.ComponentProps<typeof StatCard>['slots']>;
type ReviewsCardSlots = NonNullable<
  React.ComponentProps<typeof ReviewsCard>['slots']
>;
type ReviewsContainerSlots = NonNullable<
  React.ComponentProps<typeof ReviewsContainer>['slots']
>;

export type ReviewsSectionSlots = {
  root?: React.ComponentProps<typeof SectionContainer>;
  header?: React.ComponentProps<typeof SectionHeader>;
  statsContainer?: React.ComponentProps<typeof Flex>;
  statCard?: StatCardSlots;
  reviewsCard?: ReviewsCardSlots;
  reviewsContainer?: ReviewsContainerSlots;
};

export type ReviewsSectionProps = {
  eyebrow?: string | null;
  title?: string | null;
  description?: string | null;
  hideRating?: boolean;
  ctaText?: string;
  productId?: Queries.Maybe<string>;
  powerReviewsFlag?: Queries.Maybe<Queries.ContentfulFlag001>;
  aggregateAllPowerReviews?: boolean;
  slots?: ReviewsSectionSlots;
};

const ReviewsSection = ({
  productId,
  eyebrow,
  title,
  description,
  hideRating = false,
  ctaText,
  powerReviewsFlag,
  aggregateAllPowerReviews = false,
  slots = {},
}: ReviewsSectionProps) => {
  const [mount, setMount] = useState<boolean | null>(false);
  const reviewProductId = aggregateAllPowerReviews
    ? AGGREGATE_POWER_REVIEWS_PRODUCT_ID
    : productId ?? '';
  const {
    averageRating,
    reviewCount,
    recommendedRatio,
    ratingHistogram,
    name,
  } = usePowerReviewsById(reviewProductId);

  const productName = name?.includes('Everlywell 360')
    ? 'Everlywell 360'
    : name ?? '';

  useEffect(() => {
    if (powerReviewsFlag) {
      const { experimentId, experimentVariation: variation } =
        powerReviewsFlag.metadata?.context ?? {};

      if (!experimentId) {
        setMount(powerReviewsFlag.value);

        return;
      }

      setMount(true);
    } else {
      setMount(true);
    }
  }, [powerReviewsFlag]);

  if (!mount) {
    return null;
  }

  return (
    <SectionContainer size={['sm', null, 'xl']} {...slots.root}>
      <Flex flexDirection="column" alignItems="center" id="ReviewsSection">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          subtitle={description}
          headerAlignment={['center', null, null, 'left']}
          eyebrowAlignment={['center', null, null, 'left']}
          subtitleAlignment={['center', null, null, 'left']}
          headingSize={['2xl', null, null, 'xl']}
          {...slots.header}
        />
        <Flex
          flexDirection={['column', 'row']}
          gap={[3, 4]}
          width={'100%'}
          justifyContent={'center'}
          {...slots.statsContainer}
        >
          {!hideRating && (
            <StatCard
              recommendedRatio={recommendedRatio ?? 0}
              slots={slots.statCard}
            />
          )}
          <ReviewsCard
            rating={averageRating}
            total={reviewCount}
            histogram={
              ratingHistogram as [number, number, number, number, number]
            }
            slots={slots.reviewsCard}
          />
        </Flex>
        {!aggregateAllPowerReviews && productId && (
          <ReviewsContainer
            productId={productId}
            name={productName}
            buttonText={ctaText}
            slots={slots.reviewsContainer}
          />
        )}
      </Flex>
    </SectionContainer>
  );
};

export default ReviewsSection;
